import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import "./IssueTrackerPage.css";

const IssueTrackerPage = () => {
  const navigate = useNavigate();
  localStorage.setItem("redirectAfterLogin", window.location.pathname);

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [user, setUser] = useState(null);

  const [message, setMessage] = useState(null);

  // Issue list
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState("open"); // open | closed | all
  const [priorityFilter, setPriorityFilter] = useState("all"); // all | low | medium | high | critical
  const [search, setSearch] = useState("");

  // Create form
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newIssue, setNewIssue] = useState({
    title: "",
    description: "",
    priority: "medium",
    labels: "", // comma separated
  });

  // Update (inline)
  const [updatingId, setUpdatingId] = useState(null);

  // ---- Auth helpers (same pattern as DevPanel) ----
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("refreshToken");
    setUser(null);
    setUnauthorized(true);
    navigate("/login");
  };

  const refreshAccessToken = async () => {
    try {
      const refreshToken = localStorage.getItem("refreshToken");
      if (!refreshToken) throw new Error("No refresh token available.");

      const response = await axios.post(
        "/api/auth/refresh-token",
        {},
        { headers: { "x-refresh-token": refreshToken } }
      );

      const { accessToken, refreshToken: newRefreshToken } = response.data || {};
      if (!accessToken) throw new Error("No access token returned.");

      localStorage.setItem("token", accessToken);
      if (newRefreshToken) localStorage.setItem("refreshToken", newRefreshToken);

      return accessToken;
    } catch (err) {
      console.error("Token refresh failed:", err);
      handleLogout();
      return null;
    }
  };

  const authorizedRequest = async (config, retry = true) => {
    try {
      return await axios({
        ...config,
        headers: {
          ...(config.headers || {}),
          ...getAuthHeaders(),
        },
      });
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 && retry) {
        const newToken = await refreshAccessToken();
        if (!newToken) throw err;
        return authorizedRequest(config, false);
      }
      throw err;
    }
  };

  // ---- Auth gate (same idea as DevPanel) ----
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await authorizedRequest({ method: "get", url: "/api/auth/dashboard" });
        const data = res.data;
        setUser(data);

        // Same security as dev panel: admin + developer only
        if (data.role !== "admin" && data.role !== "developer") {
          setUnauthorized(true);
        }
      } catch (err) {
        console.error("Error checking auth for IssueTracker:", err);
        setUnauthorized(true);
      } finally {
        setCheckingAuth(false);
      }
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-clear messages
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const fetchIssues = async () => {
    try {
      setLoadingIssues(true);
      const res = await authorizedRequest({ method: "get", url: "/api/issues" });
      setIssues(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading issues:", err);
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to load issues." });
      setIssues([]);
    } finally {
      setLoadingIssues(false);
    }
  };

  useEffect(() => {
    if (!unauthorized) fetchIssues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unauthorized]);

  const filteredIssues = useMemo(() => {
    let list = issues || [];

    if (statusFilter !== "all") {
      list = list.filter((i) => (i.status || "open") === statusFilter);
    }
    if (priorityFilter !== "all") {
      list = list.filter((i) => (i.priority || "medium") === priorityFilter);
    }

    const q = search.trim().toLowerCase();
    if (!q) return list;

    return list.filter((i) => {
      const t = (i.title || "").toLowerCase();
      const d = (i.description || "").toLowerCase();
      const labels = (i.labels || []).join(" ").toLowerCase();
      const reporter = (i.createdBy?.username || "").toLowerCase();
      return t.includes(q) || d.includes(q) || labels.includes(q) || reporter.includes(q);
    });
  }, [issues, statusFilter, priorityFilter, search]);

  const priorityBadge = (p) => {
    const v = String(p || "medium").toLowerCase();
    return `priority-pill priority-${v}`;
  };

  const statusBadge = (s) => {
    const v = String(s || "open").toLowerCase();
    return `status-pill status-${v}`;
  };

  const createIssue = async (e) => {
    e.preventDefault();

    const title = newIssue.title.trim();
    const description = newIssue.description.trim();
    if (!title) {
      setMessage({ type: "error", text: "Title is required." });
      return;
    }

    const labels = newIssue.labels
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);

    try {
      setCreating(true);
      const res = await authorizedRequest({
        method: "post",
        url: "/api/issues",
        data: {
          title,
          description,
          priority: newIssue.priority,
          labels,
        },
      });

      const created = res.data?.issue || res.data;
      setIssues((prev) => [created, ...(prev || [])]);
      setNewIssue({ title: "", description: "", priority: "medium", labels: "" });
      setShowNew(false);
      setMessage({ type: "success", text: "Issue created." });
    } catch (err) {
      console.error("Create issue failed:", err);
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to create issue." });
    } finally {
      setCreating(false);
    }
  };

  const updateIssue = async (issueId, patch) => {
    try {
      setUpdatingId(issueId);
      const res = await authorizedRequest({
        method: "put",
        url: `/api/issues/${issueId}`,
        data: patch,
      });

      const updated = res.data?.issue || res.data;
      setIssues((prev) => (prev || []).map((i) => (i._id === issueId ? updated : i)));
      setMessage({ type: "success", text: "Issue updated." });
    } catch (err) {
      console.error("Update issue failed:", err);
      setMessage({ type: "error", text: err.response?.data?.message || "Failed to update issue." });
    } finally {
      setUpdatingId(null);
    }
  };

  if (checkingAuth) {
    return <p className="issue-page-loading">Checking access...</p>;
  }

  if (unauthorized) {
    return (
      <div className="issue-unauthorized">
        <h2>Access Restricted</h2>
        <p>This page is only available to Admins and Developers.</p>
        <button onClick={() => navigate("/")}>Back to Home</button>
      </div>
    );
  }

  return (
    <div className="issue-page">
      <Helmet>
        <title>Issue Tracker - Sintrix</title>
      </Helmet>

      <div className="issue-card">
        <div className="issue-header">
          <div>
            <h1>Issue Tracker</h1>
            {user && (
              <p className="issue-subtitle">
                Logged in as <strong>{user.username} ({user.role})</strong>
              </p>
            )}
          </div>

          <div className="issue-header-actions">
            <button className="issue-btn secondary" type="button" onClick={fetchIssues} disabled={loadingIssues}>
              {loadingIssues ? "Refreshing..." : "Refresh"}
            </button>
            <button className="issue-btn" type="button" onClick={() => setShowNew((p) => !p)}>
              {showNew ? "Close" : "New Issue"}
            </button>
          </div>
        </div>

        {message && (
          <div className={`issue-alert ${message.type === "error" ? "error" : "success"}`}>
            {message.text}
          </div>
        )}

        {showNew && (
          <form className="issue-new" onSubmit={createIssue}>
            <div className="issue-new-row">
              <div className="issue-field">
                <label>Title</label>
                <input
                  value={newIssue.title}
                  onChange={(e) => setNewIssue((p) => ({ ...p, title: e.target.value }))}
                  placeholder="Short summary of the bug"
                />
              </div>

              <div className="issue-field small">
                <label>Priority</label>
                <select
                  value={newIssue.priority}
                  onChange={(e) => setNewIssue((p) => ({ ...p, priority: e.target.value }))}
                >
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="critical">critical</option>
                </select>
              </div>
            </div>

            <div className="issue-field">
              <label>Description</label>
              <textarea
                rows={4}
                value={newIssue.description}
                onChange={(e) => setNewIssue((p) => ({ ...p, description: e.target.value }))}
                placeholder="Steps to reproduce, expected vs actual, screenshots/links, logs, etc."
              />
            </div>

            <div className="issue-field">
              <label>Labels (comma separated)</label>
              <input
                value={newIssue.labels}
                onChange={(e) => setNewIssue((p) => ({ ...p, labels: e.target.value }))}
                placeholder="ui, api, auth, payments, mobile..."
              />
            </div>

            <div className="issue-new-actions">
              <button className="issue-btn" type="submit" disabled={creating}>
                {creating ? "Creating..." : "Create Issue"}
              </button>
              <button
                className="issue-btn secondary"
                type="button"
                onClick={() => {
                  setShowNew(false);
                  setNewIssue({ title: "", description: "", priority: "medium", labels: "" });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="issue-filters">
          <div className="issue-filter">
            <label>Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="open">open</option>
              <option value="closed">closed</option>
              <option value="all">all</option>
            </select>
          </div>

          <div className="issue-filter">
            <label>Priority</label>
            <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
              <option value="all">all</option>
              <option value="low">low</option>
              <option value="medium">medium</option>
              <option value="high">high</option>
              <option value="critical">critical</option>
            </select>
          </div>

          <div className="issue-filter grow">
            <label>Search</label>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="title, description, label, reporter..." />
          </div>
        </div>

        {loadingIssues ? (
          <p className="issue-page-loading">Loading issues...</p>
        ) : filteredIssues.length === 0 ? (
          <p className="issue-empty">No issues found with the current filters.</p>
        ) : (
          <div className="issue-list">
            {filteredIssues.map((i) => (
              <div key={i._id} className="issue-item">
                <div className="issue-item-main">
                  <div className="issue-item-title-row">
                    <div className="issue-item-title">{i.title}</div>
                    <div className="issue-item-badges">
                      <span className={statusBadge(i.status)}>{i.status}</span>
                      <span className={priorityBadge(i.priority)}>{i.priority}</span>
                    </div>
                  </div>

                  {i.labels?.length ? (
                    <div className="issue-labels">
                      {i.labels.map((l) => (
                        <span key={l} className="issue-label">{l}</span>
                      ))}
                    </div>
                  ) : null}

                  {i.description ? (
                    <p className="issue-desc">{i.description}</p>
                  ) : (
                    <p className="issue-desc muted">No description.</p>
                  )}

                  <div className="issue-meta">
                    <span>
                      #{String(i.issueNumber || "").padStart(4, "0")} •{" "}
                      {i.createdAt ? new Date(i.createdAt).toLocaleString() : "N/A"}
                    </span>
                    <span>
                      Reported by <strong>{i.createdBy?.username || "Unknown"}</strong>
                    </span>
                    {i.updatedAt ? <span>Updated {new Date(i.updatedAt).toLocaleString()}</span> : null}
                  </div>
                </div>

                <div className="issue-item-actions">
                  {String(i.status).toLowerCase() === "open" ? (
                    <button
                      className="issue-btn secondary"
                      type="button"
                      disabled={updatingId === i._id}
                      onClick={() => updateIssue(i._id, { status: "closed" })}
                    >
                      {updatingId === i._id ? "Saving..." : "Close"}
                    </button>
                  ) : (
                    <button
                      className="issue-btn secondary"
                      type="button"
                      disabled={updatingId === i._id}
                      onClick={() => updateIssue(i._id, { status: "open" })}
                    >
                      {updatingId === i._id ? "Saving..." : "Reopen"}
                    </button>
                  )}

                  <button
                    className="issue-btn secondary"
                    type="button"
                    disabled={updatingId === i._id}
                    onClick={() => updateIssue(i._id, { priority: "critical" })}
                    title="Escalate to critical"
                  >
                    Escalate
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueTrackerPage;
